/**
 * API client and session (Doc 04 §2, §3).
 *
 * The access token lives in a module variable, never in localStorage. localStorage is
 * readable by every script on the origin, so one compromised dependency would exfiltrate
 * a token plus its tenant context in a single read; memory at least requires active
 * execution in the live tab. Persistence is the HttpOnly refresh cookie's job — a reload
 * is a silent refresh, not a login.
 */
import type { ProblemDetails } from '@cmp/contracts';

export const API_BASE = import.meta.env['VITE_API_BASE'] ?? 'http://127.0.0.1:58080';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  tenant_id: string;
  tenant: string;
  role: string;
  perms?: string[];
  site_ids: string[];
}

let accessToken: string | null = null;
let expiresAt = 0;
let user: SessionUser | null = null;
const listeners = new Set<() => void>();

export const getUser = (): SessionUser | null => user;
export const isAuthenticated = (): boolean => user !== null;

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
const notify = (): void => listeners.forEach((f) => f());

function setSession(token: string, expires: string, u: SessionUser): void {
  accessToken = token;
  expiresAt = Date.parse(expires);
  user = u;
  notify();
}

export function clearSession(): void {
  accessToken = null;
  expiresAt = 0;
  user = null;
  notify();
}

/** An RFC 9457 problem, carried as an Error so it can travel through Query. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails,
  ) {
    super(problem.title || `HTTP ${status}`);
  }
  /** Field errors keyed by field, for form display. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.field] = e.code;
    return out;
  }
}

async function parseProblem(res: Response): Promise<ApiError> {
  let problem: ProblemDetails = { type: 'about:blank', title: res.statusText, status: res.status };
  try {
    problem = (await res.json()) as ProblemDetails;
  } catch {
    /* not every error body is JSON */
  }
  return new ApiError(res.status, problem);
}

// One refresh at a time. Without this, five queries hitting a stale token at once would
// each rotate the refresh cookie, and rotation-with-reuse-detection would read the
// concurrent attempts as a stolen token and kill the whole session.
let refreshing: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) return false;
        const body = (await res.json()) as { access_token: string; expires_at: string; user: SessionUser };
        setSession(body.access_token, body.expires_at, body.user);
        return true;
      } catch {
        return false;
      } finally {
        // Cleared on the next tick so callers awaiting this promise all see the result.
        setTimeout(() => {
          refreshing = null;
        }, 0);
      }
    })();
  }
  return refreshing;
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  /** Set false for the login call, which has no token yet. */
  auth?: boolean;
}

/**
 * Fetches a binary response with the same auth handling as api().
 *
 * Separate rather than a flag, because the two differ in everything after the request:
 * no JSON parsing, no problem-details body to read on failure.
 */
export async function apiBlob(path: string): Promise<Blob> {
  if (accessToken && Date.now() > expiresAt - 60_000) await refresh();
  const send = async (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

  let res = await send();
  if (res.status === 401 && (await refresh())) res = await send();
  if (!res.ok) throw await parseProblem(res);
  return res.blob();
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, auth = true } = opts;

  // Refresh a token that is about to expire rather than waiting for the 401. Sixty
  // seconds of slack covers a slow request and a skewed clock.
  if (auth && accessToken && Date.now() > expiresAt - 60_000) await refresh();

  const send = async (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(auth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let res = await send();
  if (res.status === 401 && auth) {
    // One retry after a silent refresh. A second 401 means the session is genuinely gone.
    if (await refresh()) res = await send();
    else {
      clearSession();
      throw await parseProblem(res);
    }
  }
  if (!res.ok) throw await parseProblem(res);
  if (res.status === 204) return null as T;
  // An empty body on a 2xx is read as null rather than thrown at.
  //
  // TanStack Query rejects `undefined` from a query function outright, and a JSON parse
  // error puts the query into three retries before it settles — so a server that answers
  // "nothing yet" with a zero-length 200 shows up as a card that spins and then quietly
  // claims the thing was never done. The server no longer does that; this makes sure the
  // next one cannot.
  const text = await res.text();
  return (text.trim() === '' ? null : JSON.parse(text)) as T;
}

/** Like api(), but also returns the ETag — needed for optimistic locking on the draft. */
export async function apiWithETag<T>(path: string): Promise<{ data: T; etag: string | null }> {
  if (accessToken && Date.now() > expiresAt - 60_000) await refresh();
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw await parseProblem(res);
  return { data: (await res.json()) as T, etag: res.headers.get('ETag') };
}

export async function login(email: string, password: string): Promise<void> {
  const body = await api<{ access_token: string; expires_at: string; user: SessionUser }>(
    '/v1/auth/login',
    { method: 'POST', body: { email, password }, auth: false },
  );
  setSession(body.access_token, body.expires_at, body.user);
}

export interface TenantOption {
  id: string;
  name: string;
  role: string;
}

/**
 * Move the whole session to another account.
 *
 * Everything cached is keyed by resource id and none of those ids mean anything in the
 * next tenant, so the query cache is dropped rather than reconciled — a stale site list
 * from the previous account is the one thing that must not survive this call.
 */
export async function switchTenant(tenantId: string): Promise<void> {
  const body = await api<{ access_token: string; expires_at: string; user: SessionUser }>(
    '/v1/auth/switch',
    { method: 'POST', body: { tenant_id: tenantId } },
  );
  setSession(body.access_token, body.expires_at, body.user);
}

export async function logout(): Promise<void> {
  try {
    await api('/v1/auth/logout', { method: 'POST' });
  } finally {
    clearSession();
  }
}

/** Try to restore a session on boot. A reload should not mean a login. */
export async function restore(): Promise<boolean> {
  return refresh();
}

// ── Permissions (Doc 04 §2.6) ───────────────────────────────────────────────────────
// Mirrors the server's matrix. The UI hides what a role cannot do; the server is what
// actually enforces it, and this is only ever an affordance.

/**
 * What the signed-in user may do, as told by the server.
 *
 * The console used to keep its own role→permission table. Two copies of one rule is two
 * truths, and they drifted: the copy here had fallen behind and was hiding the scan and
 * vendor controls from owners who were entitled to use them. The server enforces with a
 * table, so the server is the one that reports it.
 */
export function can(perm: string): boolean {
  const perms = user?.perms ?? [];
  return perms.includes('*') || perms.includes(perm);
}
